import { Children, isValidElement, type ReactNode } from 'react';

/**
 * CSS `text-transform: uppercase` follows the element's language, and the document is lang="en".
 * English casing keeps Greek tone marks, so a name like "Νάσος" turns into "Ν'ΆΣΟΣ" (the accent
 * shows up as a stray apostrophe in the heavy italic face). Greek capitals carry no tone marks.
 * Marking a Greek string lang="el" makes the browser drop them: "ΝΑΣΟΣ".
 *
 *   <span className="tts-display" {...langProps(name)}>{name}</span>
 *
 * Latin text is untouched: no lang attribute is added unless the string contains Greek script.
 */
const GREEK = /[Ͱ-Ͽἀ-῿]/;

/** True when the text contains a Greek letter (basic Greek block or Greek extended). */
export function hasGreek(text: string | null | undefined): boolean {
  return !!text && GREEK.test(text);
}

/** `{ lang: 'el' }` for a string with Greek letters, `{}` otherwise. Spread it on the element that shows the text. */
export function langProps(...texts: ReadonlyArray<string | null | undefined>): { lang?: 'el' } {
  return texts.some(hasGreek) ? { lang: 'el' } : {};
}

/** Plain text of a React child tree: strings and numbers joined, elements walked. */
export function textOf(node: ReactNode): string {
  let out = '';
  Children.forEach(node, (child) => {
    if (typeof child === 'string' || typeof child === 'number') out += String(child);
    else if (isValidElement<{ children?: ReactNode }>(child)) out += textOf(child.props.children);
  });
  return out;
}
